// ============================================================================
// DASHBOARD RESUMO GERAL — Hub da Federação (glassmorphism)
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/DashboardResumoGeral.tsx
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Building2, BookOpen, DollarSign,
  AlertCircle, Activity, Eye, EyeOff,
  ArrowUpRight, ArrowDownRight, Clock,
  RefreshCw, GraduationCap, UserPlus,
} from 'lucide-react';
import apiClient from '../../services/api';
import GlassBackdrop from './ui/GlassBackdrop';
import { glass, text } from './ui/theme';

// ─── Types ───────────────────────────────────────────────────────────────────
interface KPIData {
  totalFiliados:       number;
  afiliadosAtivos:     number;
  afiliadosPendentes:  number;
  totalAlunos:         number;
  cursosHomologados:   number;
  cursosPendentes:     number;
  aprovacoesPendentes: number;
  evasoesPendentes:    number;
  receitaTotal:        number;
}

interface AtividadeRecente {
  id:       string;
  tipo:     'affiliate' | 'student' | 'course' | 'revalida' | 'default';
  mensagem: string;
  hora:     string;
  subtexto?: string;
}

interface DashboardOverview {
  affiliatesTotal?:    number;
  affiliatesActive?:   number;
  affiliatesPending?:  number;
  studentsTotal?:      number;
  coursesApproved?:    number;
  coursesPending?:     number;
  pendingApprovals?:   number | Record<string, number>;
  evasionPending?:     number;
  evasionActive?:      number;
  revenueTotal?:       number;
}
interface StatsResponse { active?: number; pending?: number; total?: number; approved?: number; }

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatRelativeTime(dateStr: string): string {
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Agora mesmo';
    if (m < 60) return `Há ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `Há ${h}h`;
    const d = Math.floor(h / 24);
    if (d < 30) return `Há ${d}d`;
    return new Date(dateStr).toLocaleDateString('pt-BR');
  } catch { return '—'; }
}

function extractPendingApprovals(value: number | Record<string, number> | undefined): number {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  return value.total ?? value.affiliates ?? value.courses ??
    Object.values(value).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0);
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
interface KPICardProps {
  titulo: string; valor: string | number; subtitulo: string;
  variacao?: number; Icon: React.ElementType;
  loading?: boolean; ocultar?: boolean; isMoney?: boolean;
}

const KPICard: React.FC<KPICardProps> = ({
  titulo, valor, subtitulo, variacao, Icon, loading, ocultar, isMoney,
}) => {
  const isPositive = (variacao ?? 0) > 0;
  const display = ocultar ? '•••'
    : isMoney ? `R$ ${Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`
    : valor;
  return (
    <div className={`${glass.card} ${glass.cardHover} p-6`}>
      <div className="flex items-start justify-between mb-5">
        <p className={text.label}>{titulo}</p>
        <Icon className="w-4 h-4 text-gold-600" strokeWidth={1.75} />
      </div>
      {loading
        ? <div className="h-9 w-24 bg-cosmos-100 rounded-lg animate-pulse" />
        : <p className={text.value}>{display}</p>}
      <div className="flex items-center justify-between mt-3">
        {loading
          ? <div className="h-3.5 w-32 bg-cosmos-100 rounded animate-pulse mt-2" />
          : <>
              <span className={text.subtle}>{subtitulo}</span>
              {variacao !== undefined && (
                <span className={`flex items-center gap-0.5 text-xs font-semibold ${isPositive ? 'text-emerald-600' : 'text-rose-500'}`}>
                  {isPositive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                  {Math.abs(variacao).toFixed(1)}%
                </span>
              )}
            </>}
      </div>
    </div>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────
const DashboardResumoGeral: React.FC = () => {
  const [periodo, setPeriodo]               = useState<'dia' | 'semana' | 'mes' | 'ano'>('mes');
  const [ocultarSensivel, setOcultarSensivel] = useState(false);
  const [kpis, setKpis] = useState<KPIData>({
    totalFiliados:0, afiliadosAtivos:0, afiliadosPendentes:0,
    totalAlunos:0, cursosHomologados:0, cursosPendentes:0,
    aprovacoesPendentes:0, evasoesPendentes:0, receitaTotal:0,
  });
  const [atividades,      setAtividades]      = useState<AtividadeRecente[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);

  // ─── Fetch KPIs ───────────────────────────────────────────────────────────
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, globalRes, affiliateRes, studentRes, courseRes] =
        await Promise.allSettled([
          apiClient.get<DashboardOverview>(`/admin/dashboard?period=${periodo}`),
          apiClient.get<DashboardOverview>(`/admin/stats/global?period=${periodo}`),
          apiClient.get<StatsResponse>('/admin/stats/affiliates'),
          apiClient.get<StatsResponse>('/admin/stats/students'),
          apiClient.get<StatsResponse>('/admin/stats/courses'),
        ]);

      const dash      = dashRes.status      === 'fulfilled' ? dashRes.value      : {} as DashboardOverview;
      const global    = globalRes.status    === 'fulfilled' ? globalRes.value    : {} as DashboardOverview;
      const affiliate = affiliateRes.status === 'fulfilled' ? affiliateRes.value : {} as StatsResponse;
      const student   = studentRes.status   === 'fulfilled' ? studentRes.value   : {} as StatsResponse;
      const course    = courseRes.status    === 'fulfilled' ? courseRes.value    : {} as StatsResponse;

      setKpis({
        totalFiliados:       affiliate.total   ?? global.affiliatesTotal   ?? 0,
        afiliadosAtivos:     affiliate.active  ?? global.affiliatesActive  ?? dash.affiliatesActive  ?? 0,
        afiliadosPendentes:  affiliate.pending ?? global.affiliatesPending ?? dash.affiliatesPending ?? 0,
        totalAlunos:         student.total     ?? global.studentsTotal     ?? dash.studentsTotal     ?? 0,
        cursosHomologados:   course.approved   ?? global.coursesApproved   ?? dash.coursesApproved   ?? 0,
        cursosPendentes:     course.pending    ?? global.coursesPending    ?? dash.coursesPending    ?? 0,
        aprovacoesPendentes: extractPendingApprovals(dash.pendingApprovals ?? global.pendingApprovals),
        evasoesPendentes:    dash.evasionPending ?? global.evasionActive ?? 0,
        receitaTotal:        dash.revenueTotal   ?? global.revenueTotal  ?? 0,
      });
    } catch { /* mantém zeros */ }
    finally { setLoading(false); }
  }, [periodo]);

  // ─── Fetch Atividades — busca de endpoints reais ──────────────────────────
  const fetchActivity = useCallback(async () => {
    setLoadingActivity(true);
    const items: AtividadeRecente[] = [];

    try {
      const notifData: any = await apiClient.get('/notifications?page=1&limit=10').catch(() => null);
      const notifs: any[] = notifData?.notifications ?? notifData?.data ?? [];
      notifs.slice(0, 5).forEach((n: any) => {
        items.push({
          id:       n.id ?? Math.random().toString(),
          tipo:     n.type?.includes('student') ? 'student'
                  : n.type?.includes('course')  ? 'course'
                  : n.type?.includes('revalida') ? 'revalida'
                  : 'affiliate',
          mensagem: n.title ?? n.message ?? n.description ?? 'Nova notificação',
          hora:     formatRelativeTime(n.createdAt ?? n.created_at ?? new Date().toISOString()),
          subtexto: n.body ?? n.subtitle ?? '',
        });
      });
    } catch { /* silencioso */ }

    if (items.length === 0) {
      try {
        const affData: any = await apiClient.get('/affiliates?page=1&limit=5&sort=createdAt');
        const affs: any[] = affData?.affiliates ?? affData?.results ?? [];
        affs.forEach((a: any) => {
          items.push({
            id:       a.id,
            tipo:     'affiliate',
            mensagem: `Novo filiado: ${a.schoolName ?? a.nomeSolicitante ?? 'Cadastro realizado'}`,
            hora:     formatRelativeTime(a.createdAt ?? new Date().toISOString()),
            subtexto: [a.city, a.state].filter(Boolean).join(', '),
          });
        });
      } catch { /* silencioso */ }
    }

    if (items.length < 5) {
      try {
        const stuData: any = await apiClient.get('/students?page=1&limit=5');
        const stus: any[] = stuData?.students ?? stuData?.data ?? [];
        stus.slice(0, 5 - items.length).forEach((s: any) => {
          items.push({
            id:       s.id,
            tipo:     'student',
            mensagem: `Novo aluno: ${s.fullName ?? s.full_name ?? 'Cadastro realizado'}`,
            hora:     formatRelativeTime(s.createdAt ?? new Date().toISOString()),
            subtexto: s.courseName ?? '',
          });
        });
      } catch { /* silencioso */ }
    }

    setAtividades(items.slice(0, 8));
    setLoadingActivity(false);
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchActivity();
  }, [fetchDashboard, fetchActivity]);

  const handleRefresh = () => { fetchDashboard(); fetchActivity(); };

  // ─── Ícones por tipo de atividade (minimal, sem caixa colorida) ───────────
  const activityIcon = (tipo: AtividadeRecente['tipo']) => {
    const map = {
      affiliate: Building2,
      student:   Users,
      course:    BookOpen,
      revalida:  GraduationCap,
      default:   UserPlus,
    };
    return map[tipo] ?? map.default;
  };

  // ─── KPI cards ────────────────────────────────────────────────────────────
  const kpiCards: KPICardProps[] = [
    { titulo:'Total de Filiados',    valor:kpis.totalFiliados,       subtitulo:`${kpis.afiliadosAtivos} ativos · ${kpis.afiliadosPendentes} pendentes`, Icon:Building2 },
    { titulo:'Total de Alunos',      valor:kpis.totalAlunos,         subtitulo:'Cadastros ativos na plataforma', Icon:Users, ocultar:ocultarSensivel },
    { titulo:'Cursos Homologados',   valor:kpis.cursosHomologados,   subtitulo:`${kpis.cursosPendentes} pendentes de homologação`, Icon:BookOpen },
    { titulo:'Receita Total',        valor:kpis.receitaTotal,        subtitulo:'Pagamentos confirmados', Icon:DollarSign, ocultar:ocultarSensivel, isMoney:true },
    { titulo:'Aprovações Pendentes', valor:kpis.aprovacoesPendentes, subtitulo:'Aguardam análise do admin', Icon:Clock },
    { titulo:'Casos de Evasão',      valor:kpis.evasoesPendentes,    subtitulo:'Alunos em múltiplas filiações', Icon:AlertCircle },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8 relative">
      <GlassBackdrop />

      {/* HEADER */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-cosmos-900 tracking-tight mb-1">Visão Geral da Plataforma</h2>
          <p className="text-sm text-cosmos-500/80">Métricas e indicadores principais do sistema</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1 ${glass.pill} p-1`}>
            {(['dia','semana','mes','ano'] as const).map(p => (
              <button key={p} onClick={() => setPeriodo(p)}
                className={`px-3 py-1.5 rounded-xl font-medium text-sm transition-all ${
                  periodo === p ? 'bg-cosmos-900 text-white' : 'text-cosmos-600 hover:bg-white/60'
                }`}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          <button onClick={() => setOcultarSensivel(!ocultarSensivel)}
            className={`px-3 py-2 ${glass.pill} text-cosmos-600 hover:text-cosmos-900 transition-colors flex items-center gap-2 text-sm font-medium`}>
            {ocultarSensivel ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {ocultarSensivel ? 'Revelar' : 'Ocultar'}
          </button>
          <button onClick={handleRefresh}
            className={`p-2.5 ${glass.pill} text-cosmos-600 hover:text-cosmos-900 transition-colors`}>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* GRID KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {kpiCards.map(k => <KPICard key={k.titulo} {...k} loading={loading} />)}
      </div>

      {/* ATIVIDADES RECENTES */}
      <div className="grid grid-cols-1 gap-5">
        <div className={`${glass.card} p-6`}>
          <div className="flex items-center justify-between mb-5">
            <h3 className={`${text.heading} flex items-center gap-2`}>
              <Activity className="w-4 h-4 text-gold-600" strokeWidth={1.75} /> Atividades Recentes
            </h3>
            <button onClick={fetchActivity}
              className="text-xs font-medium text-cosmos-500 hover:text-cosmos-900 transition-colors flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Atualizar
            </button>
          </div>

          {loadingActivity ? (
            <div className="space-y-2">
              {[1,2,3,4].map(n => <div key={n} className="h-14 bg-cosmos-50 rounded-2xl animate-pulse" />)}
            </div>
          ) : atividades.length === 0 ? (
            <div className="py-10 text-center text-cosmos-400">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" strokeWidth={1.5} />
              <p className="text-sm font-medium">Nenhuma atividade recente</p>
              <p className="text-xs mt-1 text-cosmos-400/80">As atividades aparecerão aqui conforme o sistema for usado</p>
            </div>
          ) : (
            <div className="divide-y divide-cosmos-100/70">
              {atividades.map(a => {
                const Icon = activityIcon(a.tipo);
                return (
                  <div key={a.id} className="flex items-start gap-3 py-3.5 first:pt-0 last:pb-0">
                    <Icon className="w-4 h-4 text-cosmos-400 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-cosmos-900 line-clamp-2">{a.mensagem}</p>
                      {a.subtexto && <p className="text-xs text-cosmos-400 mt-0.5">{a.subtexto}</p>}
                    </div>
                    <span className="text-xs text-cosmos-400 flex-shrink-0 whitespace-nowrap">{a.hora}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardResumoGeral;
